import { Component, Input, OnInit } from '@angular/core';
import * as _ from 'lodash';
import { TrackerService } from '../../../../../services/tracker.service';

@Component({
  selector: 'tracker-admin-workflow-transition-tab-main',
  template: require('./tracker.workflow.transition.tab.main.component.html'),
})
export class TrackerAdminWorkflowTransitionTabMainComponent implements OnInit {
  
  @Input() toFieldElement: any; // The field element object that contains details about the transition
  @Input() extraFields: any; // A list of extra fields that may be required for the transition
  @Input() roles: any; // A list of roles that may be involved in the transition

  rolesThatCanTransitionLabel: string = '';
  requiredTransitionFieldsLabel: string = '';
  requiresApprovalChoices = { 'No': false, 'Yes': true };

  /**
   * Constructs the component and injects the required services.
   * @param trackerService
   */
  constructor(private trackerService: TrackerService) {}

  /**
   * Initializes the component by setting labels for roles and required fields.
   */
  ngOnInit() {
    this.setRolesThatCanTransitionLabel();
    this.setRequiredTransitionFieldsLabel();
  }

  /**
   * Checks if a given role is allowed to perform the transition.
   * @param role - The role to check.
   * @returns True if the role is allowed, false otherwise.
   */
  roleCanTransition(role: any): boolean {
    if (_.isEmpty(role)) {
      return false;
    }

    return !!_.find(this.toFieldElement.allowedRoles, (allowedRole: any) => allowedRole.id === role.id);
  }

  /**
   * Checks if a given field is required for the transition.
   * @param field - The field to check.
   * @returns True if the field is required, false otherwise.
   */
  isTransitionFieldRequired(field: any): boolean {
    if (_.isEmpty(field)) {
      return false;
    }

    return !!_.find(this.toFieldElement.requiredFields, (requiredField: any) => requiredField.alias === field.alias);
  }

  /**
   * Toggles a field as required or not required for the transition.
   * @param field - The field to toggle.
   */
  setRequiredTransitionField(field: any) {
    const requiredField = _.find(this.toFieldElement.requiredFields, (requiredField: any) => requiredField.alias === field.alias);

    if (requiredField) {
      _.remove(this.toFieldElement.requiredFields, (requiredField: any) => requiredField.alias === field.alias);
    } else {
      if (field.alias === '') this.toFieldElement.requiredFields = [];
      this.toFieldElement.requiredFields.push({ alias: field.alias, fieldName: field.fieldName });
    }

    this.trackerService.fields.removeNone(this.toFieldElement.requiredFields, false);
    this.setRequiredTransitionFieldsLabel();
  }

  /**
   * Updates the roles that can perform the transition, adding or removing the given role.
   * @param role - The role to update.
   */
  updateRoleThatCanTransition(role: any) {
    const allowedRole = _.find(this.toFieldElement.allowedRoles, (allowedRole: any) => allowedRole.id === role.id);

    if (allowedRole) {
      _.remove(this.toFieldElement.allowedRoles, (allowedRole: any) => allowedRole.id === role.id);
    } else {
      if (role.id === 0) this.toFieldElement.allowedRoles = [];
      this.toFieldElement.allowedRoles.push({ id: role.id, roleName: role.roleName });
    }

    this.trackerService.fields.removeNone(this.toFieldElement.allowedRoles, true);
    this.setRolesThatCanTransitionLabel();
  }

  /**
   * Sets the label for roles that can perform the transition.
   * This label is a comma-separated list of role names.
   */
  private setRolesThatCanTransitionLabel() {
    this.rolesThatCanTransitionLabel = _.map(this.toFieldElement.allowedRoles, 'roleName').join(', ');

    if (this.rolesThatCanTransitionLabel === '') this.rolesThatCanTransitionLabel = 'None';
  }

  /**
   * Sets the label for fields required for the transition.
   * This label is a comma-separated list of field names.
   */
  private setRequiredTransitionFieldsLabel() {
    this.requiredTransitionFieldsLabel = _.map(this.toFieldElement.requiredFields, 'fieldName').join(', ');

    if (this.requiredTransitionFieldsLabel === '') this.requiredTransitionFieldsLabel = 'None';
  }
}
